/* eslint-disable */
import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, forwardRef } from "react";
import { ChevronLeft, ChevronRight, AlignLeft, AlignCenter, AlignRight, Trash2, Image as ImageIcon } from "lucide-react";
import { handleEditorPaste } from "@/lib/editorPaste";

/**
 * A4 Single-Page Document Editor.
 *
 * Behavior:
 *  • Renders all content into ONE tall A4 page node (so contentEditable / undo
 *    work natively and text never overlaps page-break markers).
 *  • Visually clips the page node so only ONE page-height window is shown.
 *  • Arrow buttons (◀ ▶) + Shift+ArrowLeft/Right switch pages — toolbar stays.
 *  • Auto-fit keeps the A4 width within the viewport (min 80%, max 150%).
 *  • Clicking an image opens an align / resize / delete floating bar.
 */
const MM = 3.7795275591;
const A4_W_MM = 210;
const A4_H_MM = 297;
const PAGE_W = A4_W_MM * MM;     // 793.7px
const PAGE_H = A4_H_MM * MM;     // 1122.5px
const MIN_SCALE = 0.55;
const MAX_SCALE = 1.6;

const PaginatedEditor = forwardRef(function PaginatedEditor({
  initialHtml, onChange, onPaste,
  margins = { top: 25, bottom: 25, left: 25, right: 25 },
  font = "Inter", size = 11, lineHeight = 1.5, align = "left",
  page1Banner = null, title = "", footerText = "",
  testId = "paginated-editor", editorRef: externalRef,
  onPageCountChange,
}, ref) {
  const internalRef = useRef(null);
  const editorRef = externalRef || internalRef;
  const contentRef = useRef(null);      // Tall block holding banner + editable area
  const outerRef = useRef(null);        // Outer container for width fit
  const initialSetRef = useRef(false);
  // Debounce timer for repagination + auto-page-flip during typing. We
  // intentionally don't repaginate on every keystroke — that causes layout
  // thrash and the page-flicker that users see when typing fast.
  const inputDebounceRef = useRef(null);
  const [pageCount, setPageCount] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);   // 0-based
  const [fitScale, setFitScale] = useState(1);
  const [userZoom, setUserZoom] = useState(1);
  const [imgBar, setImgBar] = useState(null);
  // Tracks the % width of the currently-selected image for the slider.
  // We derive it from the live element on every click so the slider snaps
  // to whatever value was last applied.
  const [imgWidthPct, setImgWidthPct] = useState(100);
  const [contentH, setContentH] = useState(PAGE_H);

  const padTop = margins.top * MM;
  const padBot = margins.bottom * MM;
  const padLeft = margins.left * MM;
  const padRight = margins.right * MM;
  const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, fitScale * userZoom));

  // Public API for parent
  useImperativeHandle(ref, () => ({
    goToPage: (n) => setPageIndex(Math.max(0, Math.min(pageCount - 1, n))),
    nextPage: () => setPageIndex((p) => Math.min(pageCount - 1, p + 1)),
    prevPage: () => setPageIndex((p) => Math.max(0, p - 1)),
    pageCount, pageIndex,
  }), [pageCount, pageIndex]);

  // ---------- Auto-fit width to viewport ----------
  useLayoutEffect(() => {
    const fit = () => {
      const w = outerRef.current?.clientWidth || PAGE_W;
      // Leave ~100px gutter on each side so the negative-positioned page-flip
      // arrows render OUTSIDE the page rectangle without being clipped.
      // On narrow viewports (mobile) we skip this gutter — the arrows are
      // hidden via `hidden md:flex` and we use the top-bar buttons instead.
      const gutter = w > 720 ? 110 : 16;
      setFitScale(Math.min(1.25, Math.max(MIN_SCALE, (w - gutter) / PAGE_W)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (outerRef.current) ro.observe(outerRef.current);
    window.addEventListener("resize", fit);
    return () => { ro.disconnect(); window.removeEventListener("resize", fit); };
  }, []);

  // ---------- Block-level pagination (Word-like) ----------
  // Walk the contentEditable's top-level children. There are TWO triggers for
  // starting a new page:
  //   1. The user inserted an explicit page-break marker (<div data-page-break="1">).
  //      Everything after the marker starts on the next page. The user has
  //      total control — content above stays put even if there's room below.
  //   2. A block would naturally cross the bottom margin. We push it to the
  //      next page via a top-margin shim (auto-paginate).
  // Manual breaks are HARD breaks; we never auto-merge them away.
  const FOOTER_H = footerText ? 28 : 0;
  const reservedBottomPx = padBot + FOOTER_H;
  const reservedTopPx = padTop;

  const isPageBreak = (el) =>
    el && el.nodeType === 1 && (
      el.getAttribute("data-page-break") === "1" ||
      (el.classList && el.classList.contains("munners-pb"))
    );

  const repaginate = () => {
    const editor = editorRef.current;
    const content = contentRef.current;
    if (!editor || !content) return;
    // 1. Reset previous shims on direct children.
    const children = Array.from(editor.children);
    for (const c of children) c.style.marginTop = "";
    if (children.length === 0) return;
    // 2. Determine the Y offset (in tall-content space) where the editor body starts.
    const editorRect = editor.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const editorYInContent = (editorRect.top - contentRect.top) / Math.max(scale, 0.0001);
    // 3. Iterate children top-down.
    let nextPageBottom = PAGE_H - reservedBottomPx;
    for (const child of children) {
      const childTop = editorYInContent + child.offsetTop;
      const childHeight = child.offsetHeight;
      if (childHeight === 0) continue;
      const childBottom = childTop + childHeight;

      // --- MANUAL PAGE BREAK: hard-snap the page boundary to right after this marker.
      if (isPageBreak(child)) {
        // Push the page boundary to the marker's bottom — everything that follows
        // starts on the next logical page.
        const markerBottom = childBottom;
        // If the marker happens to sit above the current page bottom, snap the
        // page boundary forward to be just past the marker (so we don't waste
        // pages). If it sits below, snap to the page that contains the marker
        // and start the next page right after it.
        while (markerBottom > nextPageBottom) {
          nextPageBottom += PAGE_H;
        }
        // The very NEXT child should start at the top of the next page.
        // We do this by pretending the marker reached up to the page bottom:
        const pushPastMarker = nextPageBottom - markerBottom;
        if (pushPastMarker > 0) {
          // Pad the marker's bottom to fill the rest of the current page.
          child.style.marginTop = `${(child.style.marginTop ? parseInt(child.style.marginTop) : 0) + pushPastMarker}px`;
        }
        // The new page now begins; reset the next-page-bottom anchor.
        nextPageBottom += PAGE_H;
        continue;
      }

      // --- AUTOFLOW: block straddles or sits past the page boundary.
      while (childTop >= nextPageBottom + reservedBottomPx) {
        nextPageBottom += PAGE_H;
      }
      if (childBottom > nextPageBottom && childTop < nextPageBottom) {
        const pushDown = (nextPageBottom - childTop) + reservedBottomPx + reservedTopPx;
        if (pushDown > 0) child.style.marginTop = `${pushDown}px`;
        nextPageBottom += PAGE_H;
      } else if (childTop >= nextPageBottom) {
        const pushDown = reservedBottomPx + reservedTopPx;
        if (pushDown > 0) child.style.marginTop = `${pushDown}px`;
        nextPageBottom += PAGE_H;
      }
    }
  };

  // ---------- Measure content height → page count ----------
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    let raf = 0;
    let pending = false;
    const measure = () => {
      if (pending) return;
      pending = true;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        repaginate();
        // Read AFTER repaginate so we get the post-pagination height.
        const h = el.scrollHeight;
        setContentH(h);
        const next = Math.max(1, Math.ceil(h / PAGE_H));
        setPageCount(next);
        onPageCountChange?.(next);
        setPageIndex((p) => Math.min(p, next - 1));
        pending = false;
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const ed = editorRef.current;
    if (ed) ro.observe(ed);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, [margins.top, margins.bottom, margins.left, margins.right, font, size, lineHeight, page1Banner, title, footerText]);

  // ---------- Initial HTML (set once to preserve undo history) ----------
  useEffect(() => {
    if (editorRef.current && !initialSetRef.current && initialHtml != null) {
      editorRef.current.innerHTML = initialHtml || "<p>Start writing…</p>";
      initialSetRef.current = true;
    }
  }, [initialHtml, editorRef]);

  // Cleanup the input debounce timer on unmount.
  useEffect(() => () => { if (inputDebounceRef.current) clearTimeout(inputDebounceRef.current); }, []);

  // ---------- Keyboard shortcuts: Shift + ←/→ for page nav ----------
  useEffect(() => {
    const onKey = (e) => {
      if (!e.shiftKey) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setPageIndex((p) => Math.min(pageCount - 1, p + 1));
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setPageIndex((p) => Math.max(0, p - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageCount]);

  // ---------- DEPRECATED: Auto-flip page on caret movement. ----------
  // Previously the editor would automatically switch to the next page when
  // the caret left the visible viewport. Users found this jarring while
  // typing or using arrow keys, so we now leave page-flipping entirely
  // user-driven (click an arrow button, click a page dot, or press
  // Shift+←/→). The viewportRef is still used by the image toolbar for
  // positioning, so we keep the ref but stop listening to selectionchange.
  const viewportRef = useRef(null);

  // ---------- Selection-aware font size adjust ----------
  const changeFontSize = (delta) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    let node = range.startContainer;
    if (node.nodeType === 3) node = node.parentElement;
    const cs = window.getComputedStyle(node);
    const cur = parseFloat(cs.fontSize) || 16;
    const next = Math.max(8, Math.min(72, Math.round(cur + delta)));
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("fontSize", false, "7");
    editorRef.current.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = `${next}px`;
      while (f.firstChild) span.appendChild(f.firstChild);
      f.replaceWith(span);
    });
    onChange?.(editorRef.current.innerHTML);
  };

  const onKeyDown = (e) => {
    const isMod = e.metaKey || e.ctrlKey;
    if (isMod && e.shiftKey && (e.key === "+" || e.key === "=" || e.code === "Equal")) {
      e.preventDefault(); changeFontSize(2); return;
    }
    if (isMod && e.shiftKey && (e.key === "_" || e.key === "-" || e.code === "Minus")) {
      e.preventDefault(); changeFontSize(-2); return;
    }
    // Ctrl/Cmd + Enter → insert hard page break at the caret (Word-style)
    if (isMod && !e.shiftKey && e.key === "Enter") {
      e.preventDefault();
      const html = '<div class="munners-pb" data-page-break="1" contenteditable="false" '
        + 'style="margin:14px 0;border:none;border-top:2px dashed #6366f1;text-align:center;line-height:0;background:transparent;cursor:default;">'
        + '<span style="background:#eef2ff;color:#4338ca;font-size:10px;font-weight:700;padding:2px 10px;border-radius:8px;letter-spacing:0.08em;text-transform:uppercase;">— page break —</span>'
        + '</div><p><br></p>';
      document.execCommand("insertHTML", false, html);
      onChange?.(editorRef.current?.innerHTML || "");
      // Repaginate immediately so the user sees the new page appear.
      requestAnimationFrame(() => {
        try { repaginate(); } catch {}
      });
      return;
    }
  };

  // ---------- Image click toolbar ----------
  const _readImgWidthPct = (img) => {
    // Prefer the inline style.width if set (we always set %); else compute
    // from the rendered width vs editor content width.
    const styleW = (img.style.width || "").trim();
    if (styleW.endsWith("%")) {
      const v = parseFloat(styleW);
      if (!Number.isNaN(v) && v > 0) return Math.round(v);
    }
    const editorW = (editorRef.current?.clientWidth || 0) - 1;
    if (editorW > 0) return Math.max(5, Math.min(100, Math.round((img.clientWidth / editorW) * 100)));
    return 100;
  };

  const onEditorClick = (e) => {
    const img = e.target.closest("img");
    if (img && editorRef.current?.contains(img)) {
      const editorRect = editorRef.current.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      setImgBar({
        top: (imgRect.top - editorRect.top) - 38,
        left: (imgRect.left - editorRect.left) + imgRect.width / 2,
        img,
      });
      setImgWidthPct(_readImgWidthPct(img));
      editorRef.current.querySelectorAll('img[data-doc-editor-selected="1"]').forEach((p) => {
        if (p !== img) { p.style.outline = ""; delete p.dataset.docEditorSelected; }
      });
      img.dataset.docEditorSelected = "1";
      img.style.outline = "2px solid #06b6d4";
      img.style.outlineOffset = "2px";
    } else {
      const sel = editorRef.current?.querySelector('img[data-doc-editor-selected="1"]');
      if (sel) { sel.style.outline = ""; delete sel.dataset.docEditorSelected; }
      setImgBar(null);
    }
  };

  const applyImgAlign = (kind) => {
    if (!imgBar?.img) return;
    const img = imgBar.img;
    img.style.display = "block";
    // Reset both longhand margins so any inherited shorthand is cleared. Then
    // set the side we want with `auto`. We always write LONGHAND so the
    // backend parser sees `margin-left` / `margin-right` explicitly.
    img.style.margin = "";
    img.style.marginTop = "0px";
    img.style.marginBottom = "0px";
    if (kind === "left")   { img.style.marginLeft = "0px";    img.style.marginRight = "auto"; }
    if (kind === "center") { img.style.marginLeft = "auto"; img.style.marginRight = "auto"; }
    if (kind === "right")  { img.style.marginLeft = "auto"; img.style.marginRight = "0px"; }
    onChange?.(editorRef.current.innerHTML);
  };
  const applyImgWidth = (pct) => {
    if (!imgBar?.img) return;
    const clamped = Math.max(5, Math.min(100, Math.round(pct)));
    imgBar.img.style.width = `${clamped}%`;
    imgBar.img.style.height = "auto";
    setImgWidthPct(clamped);
    onChange?.(editorRef.current.innerHTML);
  };
  const deleteImg = () => {
    if (!imgBar?.img) return;
    imgBar.img.remove(); setImgBar(null);
    onChange?.(editorRef.current.innerHTML);
  };

  // Layout dimensions
  const viewportH = PAGE_H * scale;
  const viewportW = PAGE_W * scale;
  // Clip offset translates the tall content so only the current page is shown.
  const translateY = -pageIndex * PAGE_H * scale;

  return (
    <div className="paginated-editor-wrap relative" data-testid={testId} ref={outerRef}>
      {/* Top bar: page navigation + zoom (sticky) */}
      <div className="sticky top-0 z-10 pb-2 mb-2 bg-white/95 backdrop-blur">
        <div className="flex items-center justify-between gap-2 text-xs text-slate-600">
          <div className="flex items-center gap-1" data-testid="editor-page-nav">
            <button type="button"
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={pageIndex === 0}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous page (Shift+←)"
              data-testid="editor-page-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-mono text-[12px] px-2 py-1 bg-slate-100 rounded-lg" data-testid="editor-page-indicator">
              Page <b>{pageIndex + 1}</b> / {pageCount}
            </span>
            <button type="button"
              onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
              disabled={pageIndex >= pageCount - 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next page (Shift+→)"
              data-testid="editor-page-next">
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-slate-400 text-[10px] ml-2 hidden md:inline">Shift + ← / → to flip pages</span>
          </div>
          <div className="flex items-center gap-1 text-slate-500">
            <button type="button" onClick={() => setUserZoom((z) => Math.max(0.6, z - 0.1))} className="px-2 py-1 rounded hover:bg-slate-100" title="Zoom out" data-testid="editor-zoom-out">−</button>
            <span className="font-mono text-[11px] w-12 text-center">{Math.round(scale * 100)}%</span>
            <button type="button" onClick={() => setUserZoom((z) => Math.min(1.8, z + 0.1))} className="px-2 py-1 rounded hover:bg-slate-100" title="Zoom in" data-testid="editor-zoom-in">+</button>
            <button type="button" onClick={() => setUserZoom(1)} className="px-2 py-1 rounded hover:bg-slate-100" title="Reset zoom" data-testid="editor-zoom-reset">Fit</button>
          </div>
        </div>

        {/* Fixed Image Tools strip — always visible. Buttons are disabled
            (greyed out) until you click an image inside the document, then
            they target that image. */}
        <ImageToolsStrip
          hasImage={!!imgBar?.img}
          widthPct={imgWidthPct}
          onAlign={applyImgAlign}
          onWidth={applyImgWidth}
          onDelete={deleteImg}
        />
      </div>

      {/* A4 page viewport — arrow buttons sit OUTSIDE the page in the gutter
          so the user can never click a page-flip arrow by accident while
          interacting with the document. */}
      <div className="relative mx-auto" style={{ width: viewportW, maxWidth: "100%" }}>
        {/* Side nav arrows (in the gutter, OUTSIDE the page rectangle) */}
        {pageIndex > 0 && (
          <button type="button"
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            className="absolute -left-12 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center hover:bg-indigo-50 hover:border-indigo-300 transition-colors hidden md:flex"
            title="Previous page (Shift+←)" data-testid="editor-side-prev">
            <ChevronLeft className="w-5 h-5 text-slate-700" />
          </button>
        )}
        {pageIndex < pageCount - 1 && (
          <button type="button"
            onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
            className="absolute -right-12 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center hover:bg-indigo-50 hover:border-indigo-300 transition-colors hidden md:flex"
            title="Next page (Shift+→)" data-testid="editor-side-next">
            <ChevronRight className="w-5 h-5 text-slate-700" />
          </button>
        )}

        {/* Page viewport — clips the tall content to show one page */}
        <div
          ref={viewportRef}
          style={{
            width: viewportW, height: viewportH,
            minHeight: viewportH, maxHeight: viewportH,
            overflow: "hidden", position: "relative",
            background: "white", boxShadow: "0 4px 24px rgba(15,23,42,0.10)",
            borderRadius: 6,
            contain: "paint",
          }}
          data-testid="editor-page-viewport"
        >
          {/* Tall content (the entire document) — translated to show current page */}
          <div
            ref={contentRef}
            style={{
              position: "absolute", top: 0, left: 0,
              width: PAGE_W,
              transform: `scale(${scale}) translateY(${-pageIndex * PAGE_H}px)`,
              transformOrigin: "top left",
            }}
          >
            {/* Margin guides */}
            <div style={{
              position: "absolute", top: padTop, left: padLeft, right: padRight,
              height: contentH - padTop - padBot,
              border: "1px dashed rgba(148, 163, 184, 0.18)", borderRadius: 4, pointerEvents: "none",
            }} />

            {/* Banner only (only on page 1 visually — sits inside top margin).
                NOTE: We intentionally do NOT render the document title here.
                The title field is metadata (used for the download filename);
                it does not get echoed inside the document body. */}
            <div style={{ paddingTop: padTop, paddingLeft: padLeft, paddingRight: padRight, position: "relative", zIndex: 1 }}>
              {page1Banner}
            </div>

            {/* Editable surface */}
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onClick={onEditorClick}
                onKeyDown={onKeyDown}
                onInput={(e) => {
                  onChange?.(e.currentTarget.innerHTML || "");
                  // Debounce repagination so styles settle when the user pauses,
                  // never on every keystroke (would cause layout thrash).
                  // We DO NOT auto-flip to a different page on input — page
                  // navigation is 100% user-driven (click arrows / dots /
                  // press Shift+←/→). This stops the "page keeps scrolling
                  // while I type" jitter users were seeing.
                  clearTimeout(inputDebounceRef.current);
                  inputDebounceRef.current = setTimeout(() => {
                    try { repaginate(); } catch { /* noop */ }
                  }, 250);
                }}
                onPaste={onPaste || handleEditorPaste}
                data-testid={`${testId}-content`}
                style={{
                  outline: "none",
                  paddingLeft: padLeft, paddingRight: padRight,
                  paddingBottom: footerText ? padBot + 28 : padBot,
                  fontFamily: font, fontSize: `${size}pt`,
                  lineHeight: lineHeight, textAlign: align,
                  color: "#0f172a", minHeight: 120,
                  wordBreak: "break-word", overflowWrap: "anywhere",
                }}
                className="prose prose-sm max-w-none doc-editor-prose"
              />
            </div>

            {/* Page-break visualisers (faint horizontal lines at every A4 boundary) */}
            {Array.from({ length: Math.max(0, pageCount - 1) }).map((_, i) => (
              <div key={i} style={{
                position: "absolute", top: (i + 1) * PAGE_H - 1, left: 0, right: 0, height: 2,
                borderTop: "1px dashed #e2e8f0", pointerEvents: "none",
              }} />
            ))}

            {/* Footer (per page, fixed at the bottom of each A4 panel) */}
            {footerText && Array.from({ length: pageCount }).map((_, i) => (
              <div key={`f-${i}`} style={{
                position: "absolute", top: (i + 1) * PAGE_H - Math.max(28, padBot * 0.5),
                left: padLeft, right: padRight,
                textAlign: "center", fontSize: "9pt", color: "#94a3b8",
                borderTop: "1px solid #e2e8f0", paddingTop: 4, pointerEvents: "none", zIndex: 2,
              }}>{footerText}</div>
            ))}
          </div>

          {/* Image toolbar — float in viewport coordinate space when an image is selected */}
          {imgBar && (
            <div
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: "absolute",
                top: (imgBar.top * scale) - (pageIndex * PAGE_H * scale),
                left: imgBar.left * scale,
                transform: "translateX(-50%)", zIndex: 20,
                background: "white", border: "1px solid #cbd5e1", borderRadius: 10,
                padding: "4px", boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
                display: "flex", gap: 2, alignItems: "center",
              }}
              data-testid="image-toolbar"
            >
              <IBtn onClick={() => applyImgAlign("left")} title="Align left">⇤</IBtn>
              <IBtn onClick={() => applyImgAlign("center")} title="Center">⇔</IBtn>
              <IBtn onClick={() => applyImgAlign("right")} title="Align right">⇥</IBtn>
              <div style={{ width: 1, background: "#e2e8f0", height: 20, margin: "0 2px" }} />
              <IBtn onClick={() => applyImgWidth(25)}  title="25% width">¼</IBtn>
              <IBtn onClick={() => applyImgWidth(50)}  title="50% width">½</IBtn>
              <IBtn onClick={() => applyImgWidth(75)}  title="75% width">¾</IBtn>
              <IBtn onClick={() => applyImgWidth(100)} title="Full width">█</IBtn>
              <div style={{ width: 1, background: "#e2e8f0", height: 20, margin: "0 2px" }} />
              <IBtn onClick={deleteImg} title="Delete" style={{ color: "#e11d48" }}>🗑</IBtn>
            </div>
          )}
        </div>
      </div>

      {/* Page dots */}
      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="editor-page-dots">
          {Array.from({ length: pageCount }).map((_, i) => (
            <button key={i} type="button"
              onClick={() => setPageIndex(i)}
              className={`transition-all rounded-full ${i === pageIndex ? "bg-cyan-500 w-6 h-2" : "bg-slate-300 hover:bg-slate-400 w-2 h-2"}`}
              title={`Go to page ${i + 1}`} data-testid={`editor-page-dot-${i}`}
              aria-label={`Go to page ${i + 1}`} />
          ))}
        </div>
      )}
    </div>
  );
});

function IBtn({ children, onClick, title, style }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-700 text-xs font-bold"
      style={style}
    >
      {children}
    </button>
  );
}

/**
 * Always-visible image-tools strip that lives in the sticky top bar.
 *
 * Behavior:
 *  • When no image is selected, the strip is greyed out + shows a hint
 *    ("Click an image to enable").
 *  • When an image is selected (via `hasImage`), every control becomes
 *    interactive: align left/center/right, four preset widths, a fine
 *    width slider (5-100%), and a delete button. The slider position is
 *    driven by `widthPct` so it stays in sync with the live image.
 *  • Uses `onMouseDown preventDefault` everywhere so clicking a button
 *    does NOT steal selection from the underlying image (otherwise
 *    `imgBar.img` would lose its DOM reference between renders).
 */
function ImageToolsStrip({ hasImage, widthPct, onAlign, onWidth, onDelete }) {
  const disabled = !hasImage;
  const baseBtn = "px-2 py-1.5 rounded-md text-[11px] font-bold inline-flex items-center gap-1 transition-colors border";
  const enabled = "bg-white border-slate-200 text-slate-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700";
  const off = "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed";
  return (
    <div
      className={`mt-2 flex flex-wrap items-center gap-1.5 rounded-xl px-2 py-1.5 border ${disabled ? "bg-slate-50/60 border-slate-100" : "bg-orange-50/60 border-orange-200"}`}
      data-testid="image-tools-strip"
      onMouseDown={(e) => e.preventDefault()}
    >
      <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide ${disabled ? "text-slate-400" : "text-orange-700"}`}>
        <ImageIcon className="w-3.5 h-3.5" /> Image
      </span>

      <div className={`h-5 w-px ${disabled ? "bg-slate-200" : "bg-orange-200"} mx-1`} />

      <button type="button" disabled={disabled} onClick={() => onAlign("left")} className={`${baseBtn} ${disabled ? off : enabled}`} title="Align left" data-testid="img-strip-align-left">
        <AlignLeft className="w-3.5 h-3.5" />
      </button>
      <button type="button" disabled={disabled} onClick={() => onAlign("center")} className={`${baseBtn} ${disabled ? off : enabled}`} title="Center" data-testid="img-strip-align-center">
        <AlignCenter className="w-3.5 h-3.5" />
      </button>
      <button type="button" disabled={disabled} onClick={() => onAlign("right")} className={`${baseBtn} ${disabled ? off : enabled}`} title="Align right" data-testid="img-strip-align-right">
        <AlignRight className="w-3.5 h-3.5" />
      </button>

      <div className={`h-5 w-px ${disabled ? "bg-slate-200" : "bg-orange-200"} mx-1`} />

      <button type="button" disabled={disabled} onClick={() => onWidth(25)}  className={`${baseBtn} ${disabled ? off : enabled}`} title="25% width"  data-testid="img-strip-w-25">25%</button>
      <button type="button" disabled={disabled} onClick={() => onWidth(50)}  className={`${baseBtn} ${disabled ? off : enabled}`} title="50% width"  data-testid="img-strip-w-50">50%</button>
      <button type="button" disabled={disabled} onClick={() => onWidth(75)}  className={`${baseBtn} ${disabled ? off : enabled}`} title="75% width"  data-testid="img-strip-w-75">75%</button>
      <button type="button" disabled={disabled} onClick={() => onWidth(100)} className={`${baseBtn} ${disabled ? off : enabled}`} title="Full width" data-testid="img-strip-w-100">Full</button>

      <div className={`h-5 w-px ${disabled ? "bg-slate-200" : "bg-orange-200"} mx-1`} />

      <label className={`inline-flex items-center gap-1.5 text-[11px] ${disabled ? "text-slate-400" : "text-slate-600"}`}>
        <input
          type="range"
          min={5}
          max={100}
          step={1}
          value={widthPct || 100}
          disabled={disabled}
          onChange={(e) => onWidth(parseInt(e.target.value, 10))}
          className="w-28 accent-orange-500"
          data-testid="img-strip-width-slider"
          aria-label="Image width percent"
        />
        <span className="font-mono w-9 text-right">{widthPct || 100}%</span>
      </label>

      <div className={`h-5 w-px ${disabled ? "bg-slate-200" : "bg-orange-200"} mx-1`} />

      <button
        type="button"
        disabled={disabled}
        onClick={onDelete}
        className={`${baseBtn} ${disabled ? off : "bg-white border-rose-200 text-rose-600 hover:bg-rose-50"}`}
        title="Delete image"
        data-testid="img-strip-delete"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      <span className={`text-[10px] ml-auto ${disabled ? "text-slate-400 italic" : "text-orange-700 font-semibold"}`}>
        {disabled ? "Click an image in the document to enable" : "Editing selected image"}
      </span>
    </div>
  );
}

export default PaginatedEditor;
